"use client";

import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import DatePicker from "./DatePicker";
import GuestSelector, { type Guests } from "./GuestSelector";
import { toast } from "./Toaster";
import { placeLabel, localizePlaceList } from "@/i18n/places";
import { LONG_STAY_MIN_NIGHTS, STAY_LONG, STAY_PARAM } from "@/lib/long-stay";

// Key order = picker display order (Maged's call, 2026-09-29); mirrors
// TOP_LEVEL_DESTINATIONS in src/lib/compounds.ts.
const DESTINATIONS = {
  Cairo: {
    emoji: "🏙️",
    sub: "Sheikh Zayed · New Cairo · Zamalek · Downtown · Giza · Maadi · Heliopolis",
    live: true,
    // Mirrors REGION_SUBAREAS["Cairo"]. Note: Sodic Villette + Cairo
    // Festival City are folded INTO "New Cairo" (they sit inside it
    // geographically) — one umbrella chip instead of three look-alike
    // ones. expandDestination resolves each name.
    compounds: ["Sheikh Zayed", "New Cairo", "Zamalek", "Downtown", "Giza", "Maadi", "Heliopolis", "6th of October"],
  },
  "El Gouna": {
    emoji: "⛵",
    sub: "Mangroovy · Sabina · Maraya",
    live: true,
    // Red Sea / Hurghada. BirdNest units carry area='El Gouna'; the sub-chips
    // are the neighbourhood compound names (match via units.compound). Mirrors
    // SUB_ZONES["El Gouna"] in src/lib/compounds.ts.
    compounds: ["Mangroovy", "Sabina", "Maraya", "Fanadir Bay", "Cyan Residence", "G-Cribs", "Ancient Hills"],
  },
  Alexandria: {
    emoji: "🌅",
    sub: "Naby Danial · Corniche · Little Venice",
    live: true,
    compounds: ["Alexandria"],
  },
  "Red Sea": {
    emoji: "🌊",
    sub: "Ain Sokhna",
    live: true,
    compounds: ["Ain Sokhna"],
  },
  "North Coast": {
    emoji: "🏖️",
    sub: "Almaza Bay · Marassi · Fouka Bay · Gaia · Playa · City Edge · Marsa Boutique · Hacienda West · D-Bay",
    live: true,
    // Mirrors REGION_SUBAREAS["North Coast"] in src/lib/compounds.ts so the
    // hero drill-down stays aligned with the /search chip strip. Sub-zones
    // like Marina 1 / Greek fold into Marassi server-side via expandDestination.
    // Almaza Bay is listed FIRST (Maged's call, 2026-07-15) — top of the drill-down.
    compounds: ["Almaza Bay", "Marassi", "Fouka Bay", "Gaia", "Playa", "City Edge", "Marsa Boutique Hotel", "Hacienda West", "D-Bay"],
  },
} as const;

type DestKey = keyof typeof DESTINATIONS;

/** "long" = the homepage's "Monthly stays" toggle (30+ nights): the date labels
 *  read Move in / Move out, the calendar refuses stays under 30 nights, and
 *  the URL gains `stay=long`, which /search reads as "only homes with a real
 *  long-stay discount" (src/lib/long-stay.ts). Everything else —
 *  the look and the area/checkin/checkout/guests/infants contract — is shared. */
export type StayMode = "short" | "long";

export default function SearchBar({ stayMode = "short" }: { stayMode?: StayMode } = {}) {
  const t = useTranslations("searchBar");
  const isLong = stayMode === "long";
  const locale = useLocale();
  const router = useRouter();
  const [where, setWhere] = useState("");
  // Empty by default — operator asked we not pre-fill the hero with a date
  // window guests didn't choose. They'll see "Add date" and pick their own.
  // (The listing-page BookingCard still uses Jul 4-7 as a placeholder so the
  // breakdown can render something meaningful before the guest picks.)
  const [dates, setDates] = useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null,
  });
  const [guests, setGuests] = useState<Guests>({ adults: 1, children: 0, infants: 0 });
  // Switching to long stay with a shorter range already picked keeps the
  // check-in and drops the check-out, so a sub-30-night stay can't be
  // submitted as "long". Adjusted during render (React's "reset state on prop
  // change" pattern), not in an effect.
  const [prevMode, setPrevMode] = useState<StayMode>(stayMode);
  if (prevMode !== stayMode) {
    setPrevMode(stayMode);
    if (
      stayMode === "long" &&
      dates.start &&
      dates.end &&
      Math.round((+dates.end - +dates.start) / 86_400_000) < LONG_STAY_MIN_NIGHTS
    ) {
      setDates({ start: dates.start, end: null });
    }
  }
  const [showCal, setShowCal] = useState(false);
  const [showGuests, setShowGuests] = useState(false);
  const [showWhere, setShowWhere] = useState(false);
  const [step, setStep] = useState<"areas" | "compounds">("areas");
  const [activeArea, setActiveArea] = useState<DestKey | null>(null);

  // Mobile sheet — collapsed pill expands into a full-screen sheet on phones.
  const [mobileOpen, setMobileOpen] = useState(false);

  const whereWrapRef = useRef<HTMLDivElement>(null);
  const inRef = useRef<HTMLButtonElement>(null);
  const outRef = useRef<HTMLButtonElement>(null);
  const guestsRef = useRef<HTMLButtonElement>(null);
  // Mobile-sheet field anchors — separate refs so popovers anchor to the right
  // element regardless of which layout is rendered.
  const inRefMobile = useRef<HTMLButtonElement>(null);
  const outRefMobile = useRef<HTMLButtonElement>(null);
  const guestsRefMobile = useRef<HTMLButtonElement>(null);
  const whereWrapRefMobile = useRef<HTMLDivElement>(null);
  // Which date field the calendar should anchor to.
  const [calAnchor, setCalAnchor] = useState<"in" | "out" | "in-m" | "out-m">("in");

  // Close location picker on outside click (desktop popover only)
  useEffect(() => {
    if (mobileOpen) return;
    const handler = (e: MouseEvent) => {
      if (whereWrapRef.current && !whereWrapRef.current.contains(e.target as Node)) {
        setShowWhere(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mobileOpen]);

  // Lock body scroll while the mobile sheet is open
  useEffect(() => {
    if (!mobileOpen) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = orig;
    };
  }, [mobileOpen]);

  function openWhere() {
    setShowWhere(true);
    setShowCal(false);
    setShowGuests(false);
    setStep("areas");
    setActiveArea(null);
  }

  function pickArea(area: DestKey) {
    setActiveArea(area);
    setStep("compounds");
  }

  function pickCompound(compound: string) {
    setWhere(compound);
    setShowWhere(false);
  }

  function pickAreaOnly(area: DestKey) {
    setWhere(area);
    setShowWhere(false);
  }

  function go(e?: React.FormEvent) {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (where) params.set("area", where.toLowerCase());
    if (dates.start) params.set("checkin", toISO(dates.start));
    if (dates.end) params.set("checkout", toISO(dates.end));
    const headcount = guests.adults + guests.children;
    if (headcount) params.set("guests", String(headcount));
    if (guests.infants) params.set("infants", String(guests.infants));
    if (isLong) params.set(STAY_PARAM, STAY_LONG);

    const label = where || t("anywhere");
    const toastMsg = headcount
      ? t("toastSearching", { label, count: headcount })
      : t("toastSearchingAny", { label });
    toast(toastMsg);
    setMobileOpen(false);
    setTimeout(() => router.push(`/search?${params.toString()}`), 400);
  }

  const fmt = (d: Date | null) =>
    d ? d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : t("addDate");

  const toISO = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const guestText = (() => {
    const parts: string[] = [];
    if (guests.adults) parts.push(t("guestAdult", { count: guests.adults }));
    if (guests.children) parts.push(t("guestChild", { count: guests.children }));
    if (guests.infants) parts.push(t("guestInfant", { count: guests.infants }));
    return parts.length ? parts.join(" · ") : t("addGuests");
  })();

  // Compact summary for the mobile collapsed pill — keeps it readable on 375px.
  const pillSummary = (() => {
    const headcount = guests.adults + guests.children;
    const parts: string[] = [];
    if (dates.start && dates.end) {
      parts.push(
        `${dates.start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${dates.end.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
      );
    } else {
      parts.push(t("anyWeek"));
    }
    if (headcount) parts.push(t("guestCount", { count: headcount }));
    return parts.join(" · ");
  })();

  // Shared destination picker UI — used by both the desktop popover and the
  // mobile sheet. Renders the 2-step area → compound flow inline.
  const destinationPicker = (
    <>
      {step === "areas" ? (
        <div className="p-3">
          <p className="text-[10.5px] font-bold uppercase tracking-wider text-ss-muted px-2 pb-3 pt-1">
            {t("chooseDestination")}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(DESTINATIONS) as DestKey[]).map((area) => {
              const d = DESTINATIONS[area];
              return (
                <button
                  key={area}
                  type="button"
                  onClick={() => pickArea(area)}
                  className={`text-start rounded-[12px] p-3.5 border transition-all [&>*]:pointer-events-none ${
                    d.live
                      ? "border-ss-line hover:border-ss-blue hover:bg-ss-blue-50 cursor-pointer"
                      : "border-dashed border-ss-line opacity-55 cursor-not-allowed"
                  }`}
                  disabled={!d.live}
                >
                  <div className="text-2xl mb-2">{d.emoji}</div>
                  <div className="font-semibold text-[14px] text-ss-text">{placeLabel(area, locale)}</div>
                  <div className="text-[11.5px] text-ss-muted mt-0.5 leading-snug">{localizePlaceList(d.sub, locale)}</div>
                  {!d.live && (
                    <span className="inline-block mt-2 text-[10px] font-bold uppercase tracking-wider bg-ss-bg-soft text-ss-soft px-2 py-0.5 rounded-full">
                      {t("comingSoon")}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 px-4 py-3 border-b border-ss-line">
            <button
              type="button"
              onClick={() => setStep("areas")}
              className="text-ss-blue hover:text-ss-blue-dark text-sm font-semibold flex items-center gap-1"
            >
              {t("back")}
            </button>
            <span className="text-sm font-bold text-ss-text">{activeArea ? placeLabel(activeArea, locale) : null}</span>
          </div>
          <div className="p-2 max-h-[280px] overflow-y-auto">
            {/* "All of <area>" as a proper first option in the list, not a
                small underlined link in the header. */}
            {activeArea && (
              <button
                type="button"
                onClick={() => pickAreaOnly(activeArea)}
                className="w-full text-start px-3.5 py-2.5 rounded-[10px] text-sm font-semibold text-ss-blue bg-ss-blue-50/60 hover:bg-ss-blue-50 flex items-center gap-3 group cursor-pointer [&>*]:pointer-events-none mb-1"
              >
                <span className="text-base">✦</span>
                <span className="flex-1">{t("allOf", { area: placeLabel(activeArea, locale) })}</span>
                <span className="text-ss-blue text-base rtl:-scale-x-100 inline-block">→</span>
              </button>
            )}
            {activeArea &&
              DESTINATIONS[activeArea].compounds.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => pickCompound(c)}
                  className="w-full text-start px-3.5 py-2.5 rounded-[10px] text-sm font-medium text-ss-text hover:bg-ss-blue-50 hover:text-ss-blue flex items-center gap-3 group cursor-pointer [&>*]:pointer-events-none"
                >
                  <span className="text-base">📍</span>
                  <span className="flex-1">{placeLabel(c, locale)}</span>
                  <span className="text-ss-line group-hover:text-ss-blue text-base rtl:-scale-x-100 inline-block">→</span>
                </button>
              ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* MOBILE — collapsed pill that opens a full-screen sheet on tap */}
      <button
        type="button"
        onClick={() => {
          setMobileOpen(true);
          setShowWhere(false);
          setShowCal(false);
          setShowGuests(false);
        }}
        className="md:hidden w-full bg-white/95 backdrop-blur rounded-full shadow-[0_12px_40px_-12px_rgba(15,28,46,.45)] px-4 py-3 flex items-center gap-3 text-start fade-up"
      >
        <span className="text-ss-soft flex-none">
          <SearchIcon />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[14px] text-ss-text truncate">
            {where ? placeLabel(where, locale) : t("wherePlaceholder")}
          </div>
          <div className="text-[12px] text-ss-muted truncate">{pillSummary}</div>
        </div>
        <span className="flex-none bg-ss-blue text-white rounded-full w-9 h-9 grid place-items-center">
          <SearchIcon />
        </span>
      </button>

      {/* MOBILE — full-screen search sheet, portaled to document.body so it
          escapes the hero section's `isolate` stacking context (otherwise the
          sticky page header at z:50 ends up rendering on top of the sheet on
          iOS Safari, hiding the sheet header and confusing the field values). */}
      {mobileOpen && typeof document !== "undefined" &&
        createPortal(
          <div className="md:hidden fixed inset-0 z-[200] bg-ss-bg-soft flex flex-col fade-up">
            {/* Sheet header */}
            <div className="bg-white border-b border-ss-line px-4 py-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label={t("closeSearch")}
                className="w-9 h-9 grid place-items-center rounded-full hover:bg-ss-bg-soft text-xl leading-none text-ss-text"
              >
                ×
              </button>
              <h2 className="font-semibold text-[15.5px] text-ss-text">{t("searchHomes")}</h2>
              {(where || dates.start || dates.end || guests.adults > 0) && (
                <button
                  type="button"
                  onClick={() => {
                    setWhere("");
                    setDates({ start: null, end: null });
                    setGuests({ adults: 0, children: 0, infants: 0 });
                  }}
                  className="ms-auto text-ss-blue text-[13px] font-semibold"
                >
                  {t("clear")}
                </button>
              )}
            </div>

            {/* Sheet body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 text-ss-text">
              {/* WHERE */}
              <div ref={whereWrapRefMobile} className="relative">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setShowWhere((s) => !s);
                    setShowCal(false);
                    setShowGuests(false);
                    setStep("areas");
                    setActiveArea(null);
                  }}
                  className="bg-white border border-ss-line rounded-[16px] p-4 flex items-center gap-3 cursor-pointer active:bg-ss-bg-soft"
                >
                  <span className="text-ss-soft flex-none">
                    <MapIcon />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-ss-muted mb-0.5">
                      {t("where")}
                    </div>
                    <div className="font-semibold text-[15px] text-ss-text overflow-hidden text-ellipsis whitespace-nowrap">
                      {where ? placeLabel(where, locale) : t("pickDestination")}
                    </div>
                  </div>
                  {where && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setWhere("");
                      }}
                      className="text-ss-soft hover:text-ss-text text-xl leading-none flex-none"
                    >
                      ×
                    </button>
                  )}
                </div>

                {showWhere && (
                  <div className="mt-2 bg-white rounded-[16px] border border-ss-line overflow-hidden">
                    {destinationPicker}
                  </div>
                )}
              </div>

              {/* CHECK-IN / CHECK-OUT */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  ref={inRefMobile}
                  type="button"
                  onClick={() => {
                    setCalAnchor("in-m");
                    setShowCal(true);
                    setShowGuests(false);
                    setShowWhere(false);
                  }}
                  className="bg-white border border-ss-line rounded-[16px] p-4 text-start active:bg-ss-bg-soft"
                >
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-ss-muted mb-0.5">
                    {t(isLong ? "moveIn" : "checkIn")}
                  </div>
                  <div className="font-semibold text-[15px] text-ss-text">{fmt(dates.start)}</div>
                </button>
                <button
                  ref={outRefMobile}
                  type="button"
                  onClick={() => {
                    setCalAnchor("out-m");
                    setShowCal(true);
                    setShowGuests(false);
                    setShowWhere(false);
                  }}
                  className="relative bg-white border border-ss-line rounded-[16px] p-4 text-start active:bg-ss-bg-soft"
                >
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-ss-muted mb-0.5">
                    {t(isLong ? "moveOut" : "checkOut")}
                  </div>
                  <div className="font-semibold text-[15px] text-ss-text pe-7">{fmt(dates.end)}</div>
                  {(dates.start || dates.end) && (
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={t("clearDates")}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDates({ start: null, end: null });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          setDates({ start: null, end: null });
                        }
                      }}
                      className="absolute top-1/2 end-3 -translate-y-1/2 w-7 h-7 grid place-items-center rounded-full text-ss-muted hover:bg-ss-line/60 hover:text-ss-text cursor-pointer"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                    </span>
                  )}
                </button>
              </div>

              {/* GUESTS */}
              <button
                ref={guestsRefMobile}
                type="button"
                onClick={() => {
                  setShowGuests((s) => !s);
                  setShowCal(false);
                  setShowWhere(false);
                }}
                className="w-full bg-white border border-ss-line rounded-[16px] p-4 text-start active:bg-ss-bg-soft flex items-center gap-3"
              >
                <span className="text-ss-soft flex-none">
                  <GuestIcon />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-ss-muted mb-0.5">
                    {t("guests")}
                  </div>
                  <div className="font-semibold text-[15px] text-ss-text overflow-hidden text-ellipsis whitespace-nowrap">
                    {guestText}
                  </div>
                </div>
              </button>
            </div>

            {/* Sheet footer */}
            <div className="bg-white border-t border-ss-line p-4 pb-[max(16px,env(safe-area-inset-bottom))]">
              <button
                type="button"
                onClick={() => go()}
                className="w-full bg-ss-blue text-white rounded-[14px] py-3.5 font-bold inline-flex items-center justify-center gap-2 hover:bg-ss-blue-dark"
              >
                <SearchIcon /> {t("searchHomes")}
              </button>
            </div>
          </div>,
          document.body,
        )}

      {/* DESKTOP — horizontal bar. `relative z-30` so the WHERE popover (z-50
          inside this stacking context) renders above the hero stats row below
          it — the stats row also creates a stacking context via `fade-up`
          (CSS transform) and would otherwise paint on top of the popover. */}
      <form
        onSubmit={go}
        className="hidden md:grid relative z-30 bg-white/95 backdrop-blur rounded-[22px] p-2 md:grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-1 items-center max-w-[1040px] shadow-[0_20px_60px_-12px_rgba(15,28,46,.35)] fade-up"
      >
        {/* WHERE — 2-step location picker */}
        <div ref={whereWrapRef} className="relative">
          <div
            role="button"
            tabIndex={0}
            onClick={openWhere}
            onKeyDown={(e) => e.key === "Enter" && openWhere()}
            className="flex items-center gap-3 px-4 py-3 rounded-[14px] hover:bg-ss-bg-soft text-ss-text cursor-pointer"
          >
            <span className="text-ss-soft flex-none">
              <MapIcon />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] font-bold uppercase tracking-wider text-ss-muted mb-0.5">
                {t("where")}
              </div>
              <div className="font-semibold text-sm truncate">
                {where ? placeLabel(where, locale) : t("pickDestination")}
              </div>
            </div>
            {where && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setWhere("");
                }}
                className="text-ss-soft hover:text-ss-text text-lg leading-none flex-none"
              >
                ×
              </button>
            )}
          </div>

          {showWhere && (
            <div className="absolute top-[calc(100%+8px)] start-0 z-50 bg-white rounded-[18px] shadow-[0_20px_60px_-12px_rgba(15,28,46,.35)] border border-ss-line overflow-hidden w-[380px] text-ss-text">
              {destinationPicker}
            </div>
          )}
        </div>

        {/* CHECK IN */}
        <button
          ref={inRef}
          type="button"
          onClick={() => {
            setCalAnchor("in");
            setShowCal(true);
            setShowGuests(false);
            setShowWhere(false);
          }}
          className="flex items-center gap-3 px-4 py-3 rounded-[14px] hover:bg-ss-bg-soft text-ss-text text-start md:border-s border-ss-line"
        >
          <span className="text-ss-soft flex-none">
            <CalIcon />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-ss-muted mb-0.5">
              {t(isLong ? "moveIn" : "checkIn")}
            </div>
            <div className="font-semibold text-sm">{fmt(dates.start)}</div>
          </div>
        </button>

        {/* CHECK OUT */}
        <button
          ref={outRef}
          type="button"
          onClick={() => {
            setCalAnchor("out");
            setShowCal(true);
            setShowGuests(false);
            setShowWhere(false);
          }}
          className="relative flex items-center gap-3 px-4 py-3 rounded-[14px] hover:bg-ss-bg-soft text-ss-text text-start md:border-s border-ss-line"
        >
          <span className="text-ss-soft flex-none">
            <CalIcon />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-ss-muted mb-0.5">
              {t(isLong ? "moveOut" : "checkOut")}
            </div>
            <div className="font-semibold text-sm">{fmt(dates.end)}</div>
          </div>
          {/* Airbnb-style clear-dates X — only when at least one date is set.
              Rendered as a <span> (not a nested button — would invalidate the
              parent <button>) with role=button so it's still keyboard-focusable.
              Click bubbling is stopped so the calendar doesn't re-open. */}
          {(dates.start || dates.end) && (
            <span
              role="button"
              tabIndex={0}
              aria-label={t("clearDates")}
              onClick={(e) => {
                e.stopPropagation();
                setDates({ start: null, end: null });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  setDates({ start: null, end: null });
                }
              }}
              className="ms-1 w-6 h-6 grid place-items-center rounded-full text-ss-muted hover:bg-ss-line/60 hover:text-ss-text cursor-pointer flex-none"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </span>
          )}
        </button>

        {/* GUESTS */}
        <button
          ref={guestsRef}
          type="button"
          onClick={() => {
            setShowGuests(true);
            setShowCal(false);
            setShowWhere(false);
          }}
          className="flex items-center gap-3 px-4 py-3 rounded-[14px] hover:bg-ss-bg-soft text-ss-text text-start md:border-s border-ss-line"
        >
          <span className="text-ss-soft flex-none">
            <GuestIcon />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-ss-muted mb-0.5">
              {t("guests")}
            </div>
            <div className="font-semibold text-sm truncate">{guestText}</div>
          </div>
        </button>

        {/* SEARCH */}
        <button
          type="submit"
          className="bg-ss-blue text-white rounded-[14px] px-7 py-3 font-bold inline-flex items-center justify-center gap-2 hover:bg-ss-blue-dark transition-colors h-full"
        >
          <SearchIcon /> {t("search")}
        </button>
      </form>

      {/* Shared popovers — anchor to whichever layout is active */}
      {showCal && (
        <DatePicker
          value={dates}
          onChange={setDates}
          onClose={() => setShowCal(false)}
          anchorRef={
            calAnchor === "out"
              ? outRef
              : calAnchor === "in-m"
                ? inRefMobile
                : calAnchor === "out-m"
                  ? outRefMobile
                  : inRef
          }
          locale={locale === "ar" ? "ar" : "en"}
          minNights={isLong ? LONG_STAY_MIN_NIGHTS : undefined}
        />
      )}
      {showGuests && (
        <GuestSelector
          value={guests}
          onChange={setGuests}
          onClose={() => setShowGuests(false)}
          anchorRef={mobileOpen ? guestsRefMobile : guestsRef}
          adultsOnly
          locale={locale === "ar" ? "ar" : "en"}
        />
      )}
    </>
  );
}

function MapIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <circle cx="12" cy="11" r="3" />
    </svg>
  );
}
function CalIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}
function GuestIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}
